import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './lib/api';
import '@/App.css';

// Pages
import { HomePage } from './pages/HomePage';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { TestsListPage, TestDetailPage } from './pages/TestsPages';
import { GamePage } from './pages/GamePage';
import { DashboardPage } from './pages/DashboardPage';
import { PricingPage, PaymentSuccessPage, GuideSuccessPage } from './pages/PricingPage';
import { GuideClaimPage } from './pages/GuideClaimPage';
import { AdminDashboardPage, AdminUsersPage, AdminTestsPage } from './pages/AdminPages';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import SkillsPage from './pages/SkillsPage';
import FAQPage from './pages/FAQPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordResetPages';
import { UnsubscribePage } from './pages/UnsubscribePage';
import { ResourcesPage } from './pages/ResourcesPage';
import { ArticlePage } from './pages/ArticlePage';
import { ForumSnippetsPage } from './pages/ForumSnippetsPage';

// Components
import { ForcePasswordChangeModal } from './components/ForcePasswordChangeModal';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAuth = false, requireAdmin = false }) => {
  const { user, loading, refreshUser } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = React.useState(false);

  // Check if user needs to change password
  React.useEffect(() => {
    if (user && user.must_change_password) {
      setShowPasswordModal(true);
    } else {
      setShowPasswordModal(false);
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B1D51]" />
      </div>
    );
  }

  if (requireAuth && !user) {
    return <Navigate to="/login" state={{ from: window.location.pathname }} />;
  }

  if (requireAdmin && (!user || !user.is_admin)) {
    return <Navigate to="/" />;
  }

  // Show password change modal if required
  if (showPasswordModal && user?.must_change_password) {
    return (
      <>
        {children}
        <ForcePasswordChangeModal 
          onSuccess={(updatedUser) => {
            setShowPasswordModal(false);
            refreshUser();
          }} 
        />
      </>
    );
  }

  return children;
};

// Main App with Routes
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/tests" element={<TestsListPage />} />
      <Route path="/tests/:slug" element={<TestDetailPage />} />
      <Route path="/play/:slug/:level" element={<GamePage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/guide/success" element={<GuideSuccessPage />} />
      <Route path="/guide/claim" element={<GuideClaimPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/skills" element={<SkillsPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/resources/forum-snippets" element={<ForumSnippetsPage />} />
      <Route path="/resources/:slug" element={<ArticlePage />} />

      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute requireAuth>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />

      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute requireAuth requireAdmin>
            <AdminDashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute requireAuth requireAdmin>
            <AdminUsersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/tests" 
        element={
          <ProtectedRoute requireAuth requireAdmin>
            <AdminTestsPage />
          </ProtectedRoute>
        } 
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            style: {
              fontFamily: 'Manrope, sans-serif',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
